/**
 * Team MV2 query API — isolated from MV1 pages/Team.jsx.
 * Per-endpoint fetchers (MV1-style resilience).
 */
import api, { getTeamSettings, getTeamOpsSnapshot } from "@/lib/api";

export const TEAM_SETTINGS_QUERY_KEY = "team-settings";
export const TEAM_SETTINGS_DATA_QUERY_KEY = "team-settings-data";
export const TEAM_ROSTER_QUERY_KEY = "team-roster";
export const TEAM_PARTNER_INVITE_QUERY_KEY = "team-partner-invite";
export const TEAM_OPS_SNAPSHOT_QUERY_KEY = "team-ops-snapshot";

function isAbortError(error, signal) {
  if (signal?.aborted) return true;
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

async function safeSectionFetch(label, signal, fetcher, fallback) {
  try {
    const data = await fetcher();
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return { data, error: null };
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    console.error(`Team MV2: failed to fetch ${label}:`, error);
    return { data: fallback, error };
  }
}

export async function fetchTeamSettingsDataFromApi(signal) {
  const { data } = await safeSectionFetch(
    "team-settings",
    signal,
    () => getTeamSettings().then((r) => r.data),
    null
  );
  return data;
}

export async function fetchTeamRosterFromApi(signal) {
  const { data } = await safeSectionFetch(
    "staff-members",
    signal,
    () =>
      api.get("/staff-members", { signal }).then((r) =>
        (r.data?.staff || []).filter((s) => String(s.phone || "").replace(/\D/g, ""))
      ),
    []
  );
  return data;
}

/**
 * Ops snapshot — errors propagate (not swallowed like safeSectionFetch) so the
 * Operations panel can show its own retry affordance instead of silently
 * rendering an empty card.
 */
export async function fetchTeamOpsSnapshotFromApi(signal, windowDays = 7) {
  const { data } = await getTeamOpsSnapshot(windowDays, { signal });
  return data;
}

export async function fetchTeamPartnerInviteFromApi(signal) {
  const { data } = await safeSectionFetch(
    "partner-invite",
    signal,
    () => fetch("/api/driver/partner/invite", { signal }).then((r) => r.json()),
    {}
  );
  return data;
}

/** Composed fetch — safe section fetchers; kept for guard wiring. */
export async function fetchTeamSettingsFromApi(signal) {
  const [data, roster, partnerInvite] = await Promise.all([
    fetchTeamSettingsDataFromApi(signal),
    fetchTeamRosterFromApi(signal),
    fetchTeamPartnerInviteFromApi(signal),
  ]);
  return { data, roster, partnerInvite };
}
